import { z } from 'zod/v4'
import { WorkflowNodeBaseSchema } from '../../resources/types'
// Import directly from types to avoid pulling in server-only dependencies
import {
  DEFAULT_AUDIO_TRANSCRIPTION_MODEL_ID,
  DEFAULT_MODEL_ID,
} from '@features/llm/resources/types'

const GenerateObjectInputsSchema = z.object({
  'object-definition': z.string().refine(
    (value) => {
      try {
        JSON.parse(value)
        return true
      } catch (error) {
        return false
      }
    },
    { message: 'Invalid JSON object definition' },
  ),
  prompt: z.string(),
  file: z.string().optional(),
})

// Note: Model validation is done at runtime in the command implementation
// to avoid bundling server-only dependencies into client code
const GenerateObjectOptionsSchema = z.object({
  'max-tokens': z.number().optional(),
  temperature: z.number().optional(),
  system: z.string().optional(),
  mode: z.enum(['text', 'pdf', 'image']).default('text'),
  model: z.string().default(DEFAULT_MODEL_ID),
  timezone: z.string().optional(),
})

export const GenerateObjectCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/ai'),
    cmd: z.literal('object.generate'),
    options: GenerateObjectOptionsSchema.optional(),
  }),
  inputs: GenerateObjectInputsSchema,
}).superRefine((data, ctx) => {
  const mode = data.service.options?.mode || 'text'
  if (
    (mode === 'pdf' || mode === 'image') &&
    !data.inputs.file
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `File is required when mode is ${mode}`,
      path: ['inputs', 'file'],
    })
  }
})

export type GenerateObjectCommand = z.infer<typeof GenerateObjectCommandSchema>

// -----------------------
// text.generate
// -----------------------

const GenerateTextInputsSchema = z.object({
  prompt: z.string(),
})

// Note: Model validation is done at runtime in the command implementation
const GenerateTextOptionsSchema = z.object({
  'max-tokens': z.number().optional(),
  temperature: z.number().optional(),
  system: z.string().optional(),
  model: z.string().default(DEFAULT_MODEL_ID),
})

export const GenerateTextCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/ai'),
    cmd: z.literal('text.generate'),
    options: GenerateTextOptionsSchema.optional(),
  }),
  inputs: GenerateTextInputsSchema,
})

export type GenerateTextCommand = z.infer<typeof GenerateTextCommandSchema>

// -----------------------
// audio.transcribe
// -----------------------

const TranscribeAudioInputsSchema = z.object({
  file: z.string(),
})

const TranscribeAudioOptionsSchema = z.object({
  /** Transcription LlmProvider model id (e.g. assemblyai/universal-2). */
  model: z.string().default(DEFAULT_AUDIO_TRANSCRIPTION_MODEL_ID),
  /** Hint for diarization (e.g. 2 for a two-person appraisal conversation). */
  'speakers-expected': z.number().int().positive().optional(),
})

export const TranscribeAudioCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/ai'),
    cmd: z.literal('audio.transcribe'),
    options: TranscribeAudioOptionsSchema.optional(),
  }),
  inputs: TranscribeAudioInputsSchema,
})

export type TranscribeAudioCommand = z.infer<typeof TranscribeAudioCommandSchema>

// -----------------------
// response.generate
// -----------------------

const GenerateResponseInputsSchema = z.object({
  // Typed as unknown here to keep schema client-safe; concrete typing happens
  // in the server-side command implementation.
  context: z.unknown(),
  'contact-id': z.string(),
})

const GenerateResponseOptionsSchema = z.object({})

export const GenerateResponseCommandSchema = WorkflowNodeBaseSchema.extend({
  service: z.object({
    name: z.literal('skedyul/ai'),
    cmd: z.literal('response.generate'),
    options: GenerateResponseOptionsSchema.optional(),
  }),
  inputs: GenerateResponseInputsSchema,
})

export type GenerateResponseCommand = z.infer<
  typeof GenerateResponseCommandSchema
>
